import React, { useState } from "react";
import { Link } from "react-scroll";
import emailjs from "@emailjs/browser";
import PillPalModel from "./PillPalModel.png";

function Home() {
  const [formValues, setFormValues] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setSubmitting(true);

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_4tck0ce";
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_n67k0jx";
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "Pi9bJrwl7xFZFKz3f";

    if (!serviceId || !templateId || !publicKey) {
      setSubmitting(false);
      setStatus("Email service not configured. Please set EmailJS env vars.");
      return;
    }

    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formValues.name || "Website Visitor",
          from_email: formValues.email,
          message: formValues.message,
          to_email: "pillpalsleeve@gmail.com",
        },
        { publicKey }
      );
      setStatus("Thanks! Your message has been sent.");
      setFormValues({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("Sorry, something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-gray-50 text-gray-900 scroll-smooth">
      {/* Hero Section */}
      <section className="flex flex-col md:flex-row items-center justify-between px-8 pt-40 pb-20" id="hero">
        <div className="max-w-xl">
          <h2 className="text-5xl font-bold mb-4">Never miss a dose again.</h2>
          <p className="text-lg mb-6 text-gray-700">
            Pill Pal is a smart device that helps you manage your medication with ease.
            By tracking your pills, it ensures you're always on schedule.
          </p>
          <div className="flex gap-4">
            <button className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600">Learn More</button>
            <Link to="contact" smooth={true} offset={-70}>
              <button className="border border-gray-400 px-6 py-3 rounded-lg hover:bg-gray-100">Contact Sales</button>
            </Link>
          </div>
        </div>
        <img src={PillPalModel} alt="Pills" className="rounded-2xl shadow-lg mt-8 md:mt-0 w-40 mx-auto md:mx-100" />
      </section>

      {/* Features */}
      <section id="features" className="bg-gray-100 py-20 text-center">
        <h3 className="text-4xl font-bold mb-4">Key Features</h3>
        <p className="text-gray-600 mb-12">Pill Pal is packed with features designed to make life easier and healthier.</p>
        <div className="grid md:grid-cols-4 gap-6 px-8 md:px-20">
          {[
            ["Smart Weight Tracking", "Accurately measures your pills so you always know your dosage."],
            ["Personalized Reminders", "Get timely reminders on your smartphone."],
            ["Seamless App Syncing", "Syncs with your mobile app for insights and reports."],
            ["Patent Pending Innovation", "Built on a revolutionary approach to medication management."]
          ].map(([title, desc]) => (
            <div key={title} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition">
              <h4 className="text-lg font-semibold mb-2">{title}</h4>
              <p className="text-gray-600 text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 text-center">
        <h3 className="text-4xl font-bold mb-4">How It Works</h3>
        <p className="text-gray-600 mb-12">Our simple three-step process makes medication management a breeze.</p>
        <div className="flex flex-col md:flex-row justify-center gap-8 px-8 md:px-20">
          {[
            ["1️⃣ Place & Pair", "Place your pill bottle on the Pill Pal device. It pairs automatically with our app."],
            ["2️⃣ Track & Remind", "Get real-time tracking and reminders to stay on schedule."],
            ["3️⃣ Review & Adjust", "See your medication history and adjust routines easily."]
          ].map(([title, desc]) => (
            <div key={title} className="bg-white p-6 rounded-2xl shadow-md w-full md:w-1/3">
              <h4 className="text-lg font-semibold mb-2">{title}</h4>
              <p className="text-gray-600 text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 flex justify-center bg-gray-50">
        <div className="bg-white p-10 rounded-2xl shadow-md w-full max-w-lg">
          <h3 className="text-3xl font-bold mb-2 text-center">Contact Us</h3>
          <p className="text-gray-600 mb-6 text-center">
            Have a question or want to get in touch? Fill out the form below.
          </p>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              className="border rounded-lg p-3"
              value={formValues.name}
              onChange={handleChange}
            />
            <input
              type="email"
              name="email"
              placeholder="your@email.com"
              className="border rounded-lg p-3"
              value={formValues.email}
              onChange={handleChange}
              required
            />
            <textarea
              rows="4"
              name="message"
              placeholder="Your message..."
              className="border rounded-lg p-3"
              value={formValues.message}
              onChange={handleChange}
              required
            ></textarea>
            <button
              type="submit"
              className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
            {status && (
              <p className="text-center text-sm text-gray-600">{status}</p>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center py-6 text-gray-500 border-t">
        © {new Date().getFullYear()} Pill Pal. All rights reserved.
      </footer>
    </main>
  );
}

export default Home;

